import { useEffect, useState, useRef } from 'react';
import { Book, MapPin, Star, Loader2, Plus, X, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AddressBookPicker — one-click, autocomplete address selector.
 *
 * Props:
 *   token         auth token
 *   type          "ship_from" | "ship_to"
 *   value         optional current address {name, company, street1, city, state, zip, country, phone, email}
 *   onSelect(a)   called with a chosen/typed address object
 *   testid        unique testid prefix
 *   label         optional label text
 *
 * Behavior:
 *   - Loads the merchant's saved book on mount
 *   - Shows a searchable dropdown
 *   - "Default" address (highest use_count or is_default) is preselected on first mount
 *   - When a saved address is picked, fires POST /address-book/{id}/use for auto-ranking
 *   - Selecting an item calls onSelect() so the parent form fills all fields
 */
export default function AddressBookPicker({ token, type, value, onSelect, testid = 'addr', label }) {
  const [book, setBook] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    // Unified address book — fetch ALL addresses regardless of type.
    // Both sender and recipient share the same book.
    fetch(`${API}/address-book`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : { addresses: [] })
      .then((d) => {
        const list = d.addresses || d || [];
        setBook(list);
        // Preselect: prefer an address whose type matches this picker, else the default/most-used one.
        if (list.length > 0 && (!value || !value.zip)) {
          const preferred = list.find((a) => a.type === type && a.is_default)
            || list.find((a) => a.type === type)
            || list.find((a) => a.is_default)
            || list[0];
          if (preferred) onSelect && onSelect(preferred);
        }
      })
      .catch(() => setBook([]));
    // eslint-disable-next-line
  }, [token, type]);

  // click-outside → close
  useEffect(() => {
    const h = (e) => { if (open && rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const pick = (addr) => {
    onSelect && onSelect(addr);
    fetch(`${API}/address-book/${addr.id}/use`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    setOpen(false);
    setQ('');
  };

  const matches = (book || []).filter((a) => {
    if (!q) return true;
    const s = `${a.name} ${a.company || ''} ${a.street1} ${a.city} ${a.state} ${a.zip}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const summary = value?.zip
    ? `${value.name || value.company || 'Unnamed'} · ${value.city || ''} ${value.state || ''} ${value.zip}`
    : book?.length ? `Pick from ${book.length} saved` : 'No saved addresses';

  return (
    <div ref={rootRef} className="relative" data-testid={`${testid}-root`}>
      {label && <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-left hover:border-indigo-300 transition"
        data-testid={`${testid}-toggle`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Book className="w-4 h-4 text-indigo-500 shrink-0" />
          <span className="truncate">{summary}</span>
        </span>
        <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-lg max-h-72 overflow-hidden flex flex-col" data-testid={`${testid}-panel`}>
          <div className="p-2 border-b border-slate-100 dark:border-slate-800">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search saved addresses…"
                className="w-full h-8 pl-7 pr-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs"
                data-testid={`${testid}-search`}
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {book === null ? (
              <div className="p-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
            ) : matches.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No matches. Type below or save from any shipment.</p>
            ) : (
              matches.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => pick(a)}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 border-b border-slate-50 dark:border-slate-800 last:border-b-0 flex items-start gap-2 group"
                  data-testid={`${testid}-item-${a.id.slice(0, 8)}`}
                >
                  {a.is_default ? <Star className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0 fill-amber-500" /> : <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{a.name}{a.company ? ` · ${a.company}` : ''}</p>
                    <p className="text-xs text-slate-500 truncate">{a.street1}{a.street2 ? `, ${a.street2}` : ''}, {a.city} {a.state} {a.zip}</p>
                  </div>
                  {a.use_count > 0 && <span className="text-[10px] text-slate-400">{a.use_count}×</span>}
                </button>
              ))
            )}
          </div>
          <div className="p-2 border-t border-slate-100 dark:border-slate-800 text-center">
            <a href="/dashboard/settings/security" className="text-xs text-indigo-600 hover:underline" data-testid={`${testid}-manage`}>
              Manage address book →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
